import React from 'react';
import { Button } from '@components/Button';
import { CTALink } from '@components/CTALink';
import { Flex } from '@components/Flex';
import { Columns } from '@components/Columns';
import { CTAGatsbyLink } from '@components/CTAGatsbyLink';
import { JoykitIcon } from '@components/JoykitIcon';
import { ParticleJoyLogo } from './ParticleJoyLogo';
import { GetStartedButton } from '@components/GetStartedButton/GetStartedButton';
import { getRootUrl } from '@utils/urlHelpers.util';
import {
  FooterColumns,
  FooterHeroMenuItem,
  FooterSubHeroItem,
  FooterSubHeroItems,
  SocialMediaMenuList,
  FooterColumn,
} from './Footer.styles';
import { Box } from '@components/Box';
import { useThemeMediaQuery } from '@hooks/useThemeMediaQuery';
import { Text } from '@components/typography';

const footerPageId = ({ page }: { page?: MarcomPageName }) => {
  switch (page) {
    case 'home':
      return 'Home_Footer';
    case 'wedding-website':
      return 'Wedding_Websites_Footer';
    case 'guest-list':
      return 'Guest_List_Footer';
    case 'wedding-invitations':
      return 'RSVP_Footer';
    case 'save-the-date':
      return 'Save_the_Dates_Footer';
    case 'registry':
      return 'Registry_Footer';
    case 'app':
      return 'App_Footer';
    default:
      return 'mainLayoutFooter';
  }
};

const appGetStartedMenuContent = ({ page }: { page?: MarcomPageName }) => {
  return (
    <>
      <GetStartedButton id="footer2create" page={page} className="get-started-button" variant="prime">
        Get Started
      </GetStartedButton>
      <Box marginTop={[5, 0]}>
        <Button
          id="footer2login"
          href={getRootUrl('/login')}
          forwardedAs={'a'}
          className="bottom-footer-link footer-login-button"
          variant="altPurpleSecondary"
        >
          Sign In
        </Button>
      </Box>
      <CTALink id="footer2login" href={getRootUrl('/login')} className="bottom-footer-link footer-login-link" variant="altPurple">
        Sign In
      </CTALink>
      <Box marginTop={[6, 0]}>
        <CTALink id="footer2find" href={getRootUrl('/find-a-couple')} className="bottom-footer-link" variant="altPurple">
          Find a Couple
        </CTALink>
      </Box>
    </>
  );
};

export const Footer: React.FC<{ page?: MarcomPageName }> = ({ page }) => {
  const isMobile = useThemeMediaQuery('below_sm');

  return (
    <footer id={footerPageId({ page })}>
      <div className="footer-menu-container">
        <FooterColumns>
          <FooterColumn desktop="half" mobile="full">
            <div className="footer-hero-menu">
              <FooterHeroMenuItem>
                <CTAGatsbyLink id="footer2weddingWebsite" to="/wedding-website" variant="footerAlt">
                  Wedding Website
                </CTAGatsbyLink>
              </FooterHeroMenuItem>
              <FooterHeroMenuItem>
                <CTAGatsbyLink id="footer2guestList" to="/guest-list" variant="footerAlt">
                  Guest List
                </CTAGatsbyLink>
              </FooterHeroMenuItem>
              <FooterHeroMenuItem>
                <CTAGatsbyLink id="footer2saveTheDate" to="/save-the-date" variant="footerAlt">
                  Save the Dates
                </CTAGatsbyLink>
              </FooterHeroMenuItem>
              <FooterHeroMenuItem>
                <CTAGatsbyLink id="footer2weddingInvitations" to="/wedding-invitations" variant="footerAlt">
                  Invites & RSVP
                </CTAGatsbyLink>
              </FooterHeroMenuItem>
              <FooterHeroMenuItem>
                <CTAGatsbyLink id="footer2registry" to="/registry" variant="footerAlt">
                  Registry
                </CTAGatsbyLink>
              </FooterHeroMenuItem>
              <FooterHeroMenuItem>
                <CTAGatsbyLink id="footer2registry" to="/app" variant="footerAlt">
                  Mobile App
                </CTAGatsbyLink>
              </FooterHeroMenuItem>
            </div>
            <div className="footer-app-get-started-menu">{appGetStartedMenuContent({ page })}</div>
          </FooterColumn>
          <FooterColumn desktop="half" mobile="full">
            <Flex justifyContent={'flex-end'}>
              <div className="footer-sub-hero-menus-container">
                <div className="footer-sub-hero-menu">
                  <FooterSubHeroItem>
                    <CTALink id="footer2designs" href={getRootUrl('/designs')} variant="footerAlt">
                      Design Templates
                    </CTALink>
                  </FooterSubHeroItem>
                  <FooterSubHeroItem>
                    <CTALink id="footer2inspiration" href="https://withjoy.com/blog/" variant="footerAlt">
                      Ideas & Inspiration
                    </CTALink>
                  </FooterSubHeroItem>
                  <FooterSubHeroItem>
                    <CTALink id="footer2vendors" href="https://withjoy.com/vendors" variant="footerAlt">
                      Wedding Vendors
                    </CTALink>
                  </FooterSubHeroItem>
                  <FooterSubHeroItem>
                    <CTAGatsbyLink id="footer2faq" to="/faq" variant="footerAlt">
                      Common Questions
                    </CTAGatsbyLink>
                  </FooterSubHeroItem>
                  <FooterSubHeroItem>
                    <CTALink id="footer2help" href="https://help.withjoy.com" variant="footerAlt">
                      Help & Support
                    </CTALink>
                  </FooterSubHeroItem>
                  <FooterSubHeroItem>
                    <CTAGatsbyLink id="footer2pricing" to="/pricing" variant="footerAlt">
                      Pricing
                    </CTAGatsbyLink>
                  </FooterSubHeroItem>
                  <FooterSubHeroItem>
                    <CTAGatsbyLink id="footer2about" to="/about" variant="footerAlt">
                      About Joy
                    </CTAGatsbyLink>
                  </FooterSubHeroItem>
                </div>

                <div className="footer-sub-hero-menu">
                  {/* <FooterSubHeroItems>
                    <CTALink id="footer2updates" href="https://updates.withjoy.com" variant="footerAlt" className="footer">
                      Latest Updates
                    </CTALink>
                  </FooterSubHeroItems> */}
                  <FooterSubHeroItems>
                    <CTAGatsbyLink id="footer2newsroom" to="/newsroom" variant="footerAlt">
                      Newsroom
                    </CTAGatsbyLink>
                  </FooterSubHeroItems>
                  <FooterSubHeroItems>
                    <CTAGatsbyLink id="footer2careers" to="/careers" variant="footerAlt">
                      Careers
                    </CTAGatsbyLink>
                  </FooterSubHeroItems>
                  <FooterSubHeroItems>
                    <CTALink id="footer2contactus" href="mailto:hello@withjoy.com" variant="footerAlt">
                      Contact Us
                    </CTALink>
                  </FooterSubHeroItems>
                  <FooterSubHeroItems isTermsAndCondMarginBottom>
                    <Flex alignItems="center">
                      <CTALink id="footer2Terms" href="https://withjoy.com/eula" variant="footerAlt">
                        Terms
                      </CTALink>
                      <Text marginX={[2]} tagName="span" color="footerText" variant="featureHed17">
                        &
                      </Text>
                      <CTALink id="footer2privacy" href="https://privacy.withjoy.com" variant="footerAlt">
                        Privacy
                      </CTALink>
                    </Flex>
                  </FooterSubHeroItems>
                </div>
                <div className="footer-top-logo-wrapper">
                  <ParticleJoyLogo id="footer2brand-mobile" variant="primary" />
                </div>

                <SocialMediaMenuList>
                  <CTALink id="footer2instagram" href="https://www.instagram.com/withjoy" className="social-media-link" variant="footerAlt">
                    <JoykitIcon name="instagramSocial" color="linkFooterHover" />
                  </CTALink>
                  <CTALink
                    id="footer2pinterest"
                    href="https://www.pinterest.com/joytheapp"
                    className="social-media-link"
                    variant="footerAlt"
                  >
                    <JoykitIcon name="pinterest" color="linkFooterHover" />
                  </CTALink>
                  <CTALink id="footer2facebook" href="https://www.facebook.com/joytheapp" className="social-media-link" variant="footerAlt">
                    <JoykitIcon name="facebook" color="linkFooterHover" />
                  </CTALink>
                  <CTALink
                    id="footer2twitter"
                    href="https://twitter.com/joytheapp"
                    className="social-media-link twitter-link"
                    variant="footerAlt"
                  >
                    <JoykitIcon name="twitter" color="linkFooterHover" />
                  </CTALink>
                  {!isMobile && (
                    <CTALink
                      id="footer2twitter"
                      href="https://twitter.com/joytheapp"
                      className="social-media-link twitter-link"
                      variant="footerAlt"
                    >
                      #withjoy
                    </CTALink>
                  )}
                </SocialMediaMenuList>
              </div>
            </Flex>
          </FooterColumn>
        </FooterColumns>
      </div>

      <div className="footer-bottom-menu">
        <Columns>
          <Columns.Column width={8}>
            <div className="footer-bottom-menu-left">{appGetStartedMenuContent({ page })}</div>
          </Columns.Column>
          <Columns.Column width={4}>
            <div className="footer-bottom-menu-right">
              <ParticleJoyLogo id="footer2brand-desktop" variant="primary" />
            </div>
          </Columns.Column>
        </Columns>
      </div>
    </footer>
  );
};
